// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 80b2092b-0486-43a1-8a24-cd57c264c889

// ---------------------------------------------------------------------------
// Primordia — Template Library
// ---------------------------------------------------------------------------
// Optimum's discovery methodology currently lives in people's heads and in a
// dozen spreadsheets that get re-cut for every engagement. This screen turns it
// into an owned, versioned, reusable asset — the thing that makes engagement
// number twelve faster than engagement number one.
//
//   1. Discovery questionnaires — reusable question sets, grouped by category,
//      with each question MAPPED to the fact-sheet fields it fills in. That
//      mapping is what lets a survey answer land straight in the CMDB instead
//      of in a Word document nobody opens again.
//   2. Fact sheet templates    — preset starting points for a new application
//      ("Clinical SaaS", "On-prem clinical system with Epic interfaces"…) that
//      pre-fill hosting, likely disposition, DR tier and the questions to ask.
//   3. Runbook checklist       — the per-application migration runbook, ordered
//      by phase with an owning role, applied to a chosen application and
//      exportable to CSV for the cutover call.
//
// Ownership mirrors the Master Catalog: Optimum owns and edits the library,
// client users may read it and apply it. Persistence tries GET/POST /templates
// and falls back to localStorage; the STARTER_* constants below are compiled in,
// so the library is never empty — no API, no localStorage, still useful.
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Button, Tabs, Tab, Table, TableHead, TableRow,
  TableCell, TableBody, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Alert, Checkbox, CircularProgress, Tooltip, IconButton,
  LinearProgress, Divider, Autocomplete, List, ListItemButton, ListItemText,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LockIcon from '@mui/icons-material/Lock';
import { getConfig } from './config';
import { getIdToken, isMaster } from './auth';
import { store } from './store';
import { allFieldsFor } from './schema';
import { exportCsv, triggerDownload } from './exports';
import HelpBubble from './HelpBubble';
import { TabLabel } from './ui';

// ---- Optimum palette -------------------------------------------------------
const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const HEAD = 'sectionHeader.main';
const headSx = { '& th': { fontWeight: 700, color: HEAD } };
const btnSx = { bgcolor: BLUE, '&:hover': { bgcolor: NAVY } };

const LS_TEMPLATES = 'primordia.templates';

// ---------------------------------------------------------------------------
// Categories — the six areas a cloud-migration discovery actually has to cover.
// ---------------------------------------------------------------------------
export const TEMPLATE_CATEGORIES = [
  'Vendor & licensing',
  'Technical architecture',
  'Integration',
  'Security & access',
  'DR & business continuity',
  'Cutover',
];

const PHASES = ['Pre-migration', 'Cutover', 'Post-migration'];
const PHASE_COLOR = {
  'Pre-migration': '#0D5B9A',
  Cutover: '#C25E00',
  'Post-migration': '#2E7D32',
};

// ---------------------------------------------------------------------------
// STARTER QUESTIONNAIRES — real discovery practice, not lorem ipsum.
// `fields` is the list of Application fact-sheet field keys an answer fills in.
// An empty list is honest: the answer is context we keep, not a field we store.
// ---------------------------------------------------------------------------
const STARTER_QUESTIONNAIRES = [
  {
    id: 'qst-vendor',
    builtin: true,
    version: 1,
    name: 'Vendor & licensing review',
    category: 'Vendor & licensing',
    description:
      'Asked of the vendor, not the client. Most migrations stall here — a vendor who will not support the target platform turns a rehost into a repurchase, and that changes the plan and the price.',
    questions: [
      {
        id: 'q-v1',
        text: 'Has the vendor validated this version of the product on AWS or Azure?',
        helper: 'We want it in writing, with the specific version. "It should be fine" is not validation.',
        fields: ['cmVendorReadiness', 'vendorName', 'version'],
      },
      {
        id: 'q-v2',
        text: 'Does the vendor offer a SaaS edition that would remove the server estate altogether?',
        helper: 'If a supported SaaS edition exists, the disposition is probably repurchase, not rehost.',
        fields: ['cmSaaS', 'cmDisposition'],
      },
      {
        id: 'q-v3',
        text: 'Will the vendor support the application running on customer-managed cloud infrastructure (IaaS)?',
        helper: 'Some vendors support only their own hosting or a named data centre. Get the exception in writing.',
        fields: ['cmIaaS', 'hostingType'],
      },
      {
        id: 'q-v-planned',
        text: 'Are any upgrades, replacements or vendor changes already planned for this application in the next twelve months?',
        helper: 'A planned upgrade is not a detail, it is a sequencing constraint: migrating first and upgrading after can mean doing the work twice, and upgrading first can block the wave. Ask before the wave is drawn, not after. Taken from NGHS discovery, 2025.',
        fields: ['version', 'migrationWave', 'lifecycleState'],
      },
      {
        id: 'q-v4',
        text: 'Does the licence move with the workload, or is it tied to hardware, sockets, MAC address or a licence server?',
        helper: 'Hardware-locked licences and on-prem licence servers are a classic day-of-cutover failure.',
        fields: ['lessonsLearned', 'tco'],
      },
      {
        id: 'q-v5',
        text: 'Is the deployed version still supported, and is an upgrade required before or during the migration?',
        helper: 'An upgrade bundled into a migration doubles the testing. Decide deliberately, not by accident.',
        fields: ['version', 'lifecycleState'],
      },
      {
        id: 'q-v6',
        text: 'Who is the vendor point of contact for the migration, and how is a migration ticket raised?',
        helper: 'Name, email, and the support path. We will need them on the cutover call.',
        fields: ['vendorContact', 'vendorEmail', 'vendorWebsite'],
      },
      {
        id: 'q-v7',
        text: 'What are the vendor professional-services costs for migration support, and is a contract change needed?',
        helper: 'Captured against the Contract fact sheet as well — this is a budget line, not a technical detail.',
        fields: ['tco'],
      },
    ],
  },
  {
    id: 'qst-tech',
    builtin: true,
    version: 1,
    name: 'Technical architecture baseline',
    category: 'Technical architecture',
    description:
      'The server-by-server picture. This is what the sizing model, the wave plan and the cost estimate are all built from, so vague answers here cost money later.',
    questions: [
      {
        id: 'q-t1',
        text: 'List the servers that make up this application — application, web, database, reporting, interface, licence.',
        helper: 'Names, roles and environment. A rough list beats a perfect list that arrives after the wave is planned.',
        fields: ['serverInventory'],
      },
      {
        id: 'q-t2',
        text: 'What operating system and version does each server run?',
        helper: 'Anything out of support (2008 R2, 2012 R2, RHEL 6) needs a decision before it moves.',
        fields: ['osVersion'],
      },
      {
        id: 'q-t3',
        text: 'What database engine, edition and version does it use?',
        helper: 'SQL Server Standard vs Enterprise, Oracle SE vs EE, Caché/IRIS — the edition drives both licence and target.',
        fields: ['dbType'],
      },
      {
        id: 'q-t4',
        text: 'Is the database compatible with a managed service (AWS RDS / Azure SQL MI), or must it stay on a VM?',
        helper: 'Check for CLR, cross-database queries, linked servers, SQL Agent jobs and filestream — the usual blockers.',
        fields: ['cmRdsCompliant', 'cmRdsOrEc2'],
      },
      {
        id: 'q-t5',
        text: 'What CPU and memory does the database need at peak, and what is the vendor minimum?',
        helper: 'Vendor minimum and observed peak are usually different numbers. We want both.',
        fields: ['cmMinRam', 'peakPeriods'],
      },
      {
        id: 'q-t6',
        text: 'How much storage is in use today, how fast is it growing, and what IOPS does it need?',
        helper: 'Current size, annual growth, and any file shares or image archives attached to the application.',
        fields: ['hostingDescription'],
      },
      {
        id: 'q-t7',
        text: 'Where is it hosted today, and where should it run after the programme?',
        helper: 'Current data centre or vendor cloud, and the agreed target.',
        fields: ['hostingType', 'locationType', 'targetHosting'],
      },
      {
        id: 'q-t-access',
        text: 'How do users actually reach this application: published desktop or Citrix, a browser, an installed client, or mobile?',
        helper: 'This decides far more than it looks. An application delivered through Citrix moves when the Citrix estate moves, not when the application does, and an installed thick client means every workstation is part of the cutover. Taken from NGHS discovery, 2025.',
        fields: ['hostingType', 'locationType'],
      },
      {
        id: 'q-t8',
        text: 'Which non-production environments exist (dev, test, train, DR) and must they move too?',
        helper: 'Training environments are forgotten in scoping and then block go-live for the education team.',
        fields: ['hostingDescription', 'cmEffort'],
      },
      {
        id: 'q-t9',
        text: 'What are the normal usage hours and the peak periods?',
        helper: 'Mon–Fri 7–7, 24/7, month-end, shift change, flu season — this decides the cutover window.',
        fields: ['normalHours', 'peakPeriods'],
      },
    ],
  },
  {
    id: 'qst-integration',
    builtin: true,
    version: 1,
    name: 'Integration & interface discovery',
    category: 'Integration',
    description:
      'Applications rarely fail to migrate. Their interfaces do. Every upstream feed, downstream consumer, protocol and hardcoded address needs to be on the page before a wave is committed.',
    questions: [
      {
        id: 'q-i1',
        text: 'Which upstream systems send data into this application?',
        helper: 'ADT, orders, scheduling, HR, finance — name the system and what it sends.',
        fields: ['cmOtherIntegrations'],
      },
      {
        id: 'q-i2',
        text: 'Which downstream systems receive data from it?',
        helper: 'Results, billing, registries, data warehouse, reporting extracts.',
        fields: ['cmExternalIntegrations'],
      },
      {
        id: 'q-i3',
        text: 'Do the interfaces run through an interface engine (Epic Bridges, Rhapsody, Cloverleaf, Mirth), or point-to-point?',
        helper: 'Engine-mediated interfaces are a config change at cutover. Point-to-point means touching both ends.',
        fields: ['cmOtherIntegrations'],
      },
      {
        id: 'q-i4',
        text: 'What protocols and ports are in use — HL7 v2 over MLLP, FHIR/REST, SFTP, DICOM, database link?',
        helper: 'Protocol, direction, port number, and whether it is TLS-wrapped.',
        fields: ['firewallRules'],
      },
      {
        id: 'q-i5',
        text: 'Are there hardcoded IP addresses in devices, scripts, interface configs or client installs?',
        helper: 'Modality consoles, lab analysers and label printers are the usual offenders.',
        fields: ['hardcodedIps', 'mustWhitelist'],
      },
      {
        id: 'q-i6',
        text: 'Is the application sensitive to added network latency between tiers or to the client?',
        helper: 'Chatty client-server apps and imaging viewers degrade badly over an added 20–30 ms.',
        fields: ['latencySensitive'],
      },
      {
        id: 'q-i7',
        text: 'Do any interfaces cross the organisation boundary (vendor, payer, HIE, state registry, reference lab)?',
        helper: 'External parties need lead time and their own change window — often weeks.',
        fields: ['cmExternalIntegrations', 'mustWhitelist'],
      },
      {
        id: 'q-i8',
        text: 'Is VPN, private link or site-to-site connectivity required to reach it?',
        helper: 'Includes vendor support tunnels used for remote maintenance.',
        fields: ['vpnRequired', 'firewallRules'],
      },
    ],
  },
  {
    id: 'qst-security',
    builtin: true,
    version: 1,
    name: 'Security, access & compliance',
    category: 'Security & access',
    description:
      'Owned by InfoSec, answered with the application team. Anything holding PHI drags HIPAA, audit and access controls into the migration design rather than into a late review.',
    questions: [
      {
        id: 'q-s1',
        text: 'How do users authenticate today — Active Directory, LDAP, local accounts, badge tap, shared logins?',
        helper: 'If it is local accounts or shared logins, say so — it changes the target design.',
        fields: ['userAuth', 'clientAccess'],
      },
      {
        id: 'q-s2',
        text: 'Is multi-factor authentication in place, and is it enforced for remote and privileged access?',
        helper: 'Separate the answer for clinicians, administrators and vendor support accounts.',
        fields: ['mfa'],
      },
      {
        id: 'q-s3',
        text: 'Does the application support single sign-on, and via which providers (SAML, OIDC, Entra ID, Okta)?',
        helper: 'Supported by the product is not the same as configured here today.',
        fields: ['ssoAvailability', 'ssoProviders'],
      },
      {
        id: 'q-s4',
        text: 'Does it store, process or transmit PHI or other regulated data?',
        helper: 'Includes free-text notes, images, extracts and any interface payload passing through.',
        fields: ['phi'],
      },
      {
        id: 'q-s5',
        text: 'Which compliance standards apply — HIPAA, HITRUST, PCI-DSS, CMS, Joint Commission, state law?',
        helper: 'Name the standard and any attestation the vendor already holds.',
        fields: ['complianceStandards'],
      },
      {
        id: 'q-s6',
        text: 'What security controls exist today — encryption at rest and in transit, key management, endpoint agents, privileged access?',
        helper: 'What is actually enabled, not what the product is capable of.',
        fields: ['securityControls'],
      },
      {
        id: 'q-s7',
        text: 'What audit logging and retention is required, who reviews it, and where do logs go?',
        helper: 'Retention period, SIEM destination, and the named reviewer.',
        fields: ['auditRequirements'],
      },
      {
        id: 'q-s8',
        text: 'How do users reach the application — thick client, browser, Citrix/VDI, mobile, kiosk?',
        helper: 'The access path decides how much of the estate has to move with the application.',
        fields: ['clientAccess'],
      },
    ],
  },
  {
    id: 'qst-dr',
    builtin: true,
    version: 1,
    name: 'DR & business continuity',
    category: 'DR & business continuity',
    description:
      'Answered by the department that uses the system, in plain language. RTO and RPO stated by the business — not assumed by IT — are what justify the target DR strategy and its cost.',
    questions: [
      {
        id: 'q-d1',
        text: 'How long can your department work without this system before patient care or revenue is affected?',
        helper: 'A real number in hours. This becomes the RTO.',
        fields: ['rto'],
      },
      {
        id: 'q-d2',
        text: 'How much recent data could you afford to lose and re-enter after an outage?',
        helper: 'Minutes or hours of work. This becomes the RPO.',
        fields: ['rpo'],
      },
      {
        id: 'q-d3',
        text: 'Are there documented downtime procedures, and when were they last exercised?',
        helper: 'Paper forms, downtime PCs, printed schedules — and whether staff have actually used them.',
        fields: ['downtimeProcedures'],
      },
      {
        id: 'q-d4',
        text: 'What is the patient-safety impact if this system is unavailable for a full shift?',
        helper: 'Be specific: delayed results, cancelled procedures, manual medication checks.',
        fields: ['patientSafetyImpact'],
      },
      {
        id: 'q-d5',
        text: 'Who is the primary stakeholder for this system, and who is their director?',
        helper: 'The person who feels it first when it is down, and who we escalate to.',
        fields: ['primaryStakeholder', 'primaryStakeholderDirector', 'primaryUserRole'],
      },
      {
        id: 'q-d6',
        text: 'Does the system hold vital records that must be preserved and retrievable (legal medical record, images, signed consent)?',
        helper: 'Vital records change the backup, retention and legal-hold requirements.',
        fields: ['auditRequirements', 'phi'],
      },
      {
        id: 'q-d7',
        text: 'What DR capability exists today, and what is the agreed target strategy after migration?',
        helper: 'Backup & restore, pilot light, warm standby or active/active — and who pays for it.',
        fields: ['targetDrStrategy', 'drTier'],
      },
      {
        id: 'q-d8',
        text: 'Can the work be performed remotely during an outage or a cutover window?',
        helper: 'Matters for weekend cutovers and for planning who must be on site.',
        fields: ['remoteWork'],
      },
      {
        id: 'q-d9',
        text: 'How does the business rate the criticality of this system relative to its others?',
        helper: 'Mission critical, business critical, business operational, administrative.',
        fields: ['businessCriticality', 'drTier'],
      },
    ],
  },
  {
    id: 'qst-cutover',
    builtin: true,
    version: 1,
    name: 'Cutover planning',
    category: 'Cutover',
    description:
      'Asked once the disposition is agreed and the wave is forming. These answers become the runbook in tab 3 and the go / no-go criteria on the night.',
    questions: [
      {
        id: 'q-c1',
        text: 'How much data must move, and can it be pre-seeded with a final delta refresh at cutover?',
        helper: 'Total size, and whether the engine supports an incremental catch-up.',
        fields: ['cmEffort'],
      },
      {
        id: 'q-c2',
        text: 'What downtime window will the business accept, on which day, and who approves it?',
        helper: 'Length, day of week, and the blackout dates we must avoid.',
        fields: ['normalHours', 'rto'],
      },
      {
        id: 'q-c3',
        text: 'Which environments migrate first (dev/test/train), and how is each validated before production follows?',
        helper: 'A rehearsed non-prod migration is the cheapest insurance in the programme.',
        fields: ['cmWave'],
      },
      {
        id: 'q-c4',
        text: 'Which other applications must move in the same wave because of dependencies?',
        helper: 'Shared databases, shared interface engines, shared file shares, shared authentication.',
        fields: ['cmWave', 'cmOtherIntegrations'],
      },
      {
        id: 'q-c5',
        text: 'Who performs the smoke test after cutover, and exactly which transactions do they run?',
        helper: 'A named clinical or business tester and a written script — not "the team will check".',
        fields: ['primaryUserRole', 'primaryStakeholder'],
      },
      {
        id: 'q-c6',
        text: 'What is the rollback trigger, who makes that call, and by what time must it be made?',
        helper: 'The go / no-go clock. Without it, a bad night becomes a bad week.',
        fields: ['lessonsLearned'],
      },
      {
        id: 'q-c7',
        text: 'What monitoring and alerting must be in place before go-live, and who receives the alerts?',
        helper: 'Application, database, interface queue depth, and the on-call rota.',
        fields: ['monitoringAlerting'],
      },
      {
        id: 'q-c8',
        text: 'Who signs off that the application is live, supported and out of hypercare?',
        helper: 'Business owner and technical owner, by name.',
        fields: ['applicationOwner', 'technicalOwner'],
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// STARTER FACT SHEET TEMPLATES — preset starting points for a new application.
// `defaults` are Application field keys; `questionnaires` are the question sets
// the discovery team should run against it.
// ---------------------------------------------------------------------------
const STARTER_FACT_TEMPLATES = [
  {
    id: 'fst-clinical-saas',
    builtin: true,
    name: 'Clinical SaaS application',
    description:
      'Already vendor-hosted and clinician-facing. The work is not moving it — it is proving the contract, the SSO and the data flows, and deciding whether it stays as-is.',
    questionnaires: ['qst-vendor', 'qst-security', 'qst-integration'],
    defaults: {
      applicationType: 'Clinical',
      hostingType: 'SaaS',
      locationType: 'Vendor Cloud (SaaS)',
      targetHosting: 'Remain vendor SaaS',
      cmSaaS: true,
      cmCurrentStatus: 'Live',
      cmDisposition: 'Retain',
      sixRClassification: 'Retain',
      timeClassification: 'Tolerate',
      drTier: 'Tier 2',
      phi: true,
      ssoAvailability: 'Available',
      targetDrStrategy: 'Warm Standby',
      rto: '8 to 24 Hours',
      rpo: '4 to 8 hours',
      lifecycleState: 'Active',
    },
  },
  {
    id: 'fst-onprem-epic',
    builtin: true,
    name: 'On-prem clinical system with Epic interfaces',
    description:
      'The hardest and most common shape: on-premise, patient-facing, and wired into the interface engine in both directions. Assume tier 1 until the business proves otherwise.',
    questionnaires: ['qst-tech', 'qst-integration', 'qst-security', 'qst-dr', 'qst-cutover'],
    defaults: {
      applicationType: 'Clinical',
      hostingType: 'On Premise',
      locationType: 'On-Prem Data Center',
      targetHosting: 'AWS',
      cmCurrentStatus: 'Live',
      cmDisposition: 'Rehost',
      sixRClassification: 'Rehost',
      timeClassification: 'Migrate',
      cmWave: 'Wave 1',
      cmEffort: 'Medium: 3-4 months',
      drTier: 'Tier 1',
      phi: true,
      latencySensitive: true,
      patientSafetyImpact: 'Medium Risk - BC plans inadequate',
      rto: '< 8 Hours',
      rpo: '< 4 hours',
      targetDrStrategy: 'Warm Standby',
      downtimeProcedures: true,
      lifecycleState: 'Active',
    },
  },
  {
    id: 'fst-business-cots',
    builtin: true,
    name: 'Business COTS application',
    description:
      'Finance, HR, supply chain or facilities. No PHI, no patient-safety impact, a forgiving RTO — so it is a good wave-one rehearsal for the team and the tooling.',
    questionnaires: ['qst-vendor', 'qst-tech', 'qst-cutover'],
    defaults: {
      applicationType: 'Business',
      hostingType: 'On Premise',
      locationType: 'On-Prem Data Center',
      targetHosting: 'AWS',
      cmCurrentStatus: 'Live',
      cmDisposition: 'Replatform',
      sixRClassification: 'Replatform',
      timeClassification: 'Tolerate',
      cmWave: 'Wave 1',
      cmEffort: 'Small: 1-2 months',
      drTier: 'Tier 3',
      phi: false,
      patientSafetyImpact: 'Low Risk - BC plans in place',
      rto: '24 to 96 Hours',
      rpo: '8 to 24 Hours',
      targetDrStrategy: 'Backup & Restore',
      lifecycleState: 'Active',
    },
  },
  {
    id: 'fst-departmental',
    builtin: true,
    name: 'Departmental / homegrown application',
    description:
      'An Access database, a shared spreadsheet with macros, or a server someone built in 2011. Discovery here is mostly about finding the owner and testing whether it should move at all.',
    questionnaires: ['qst-tech', 'qst-dr'],
    defaults: {
      applicationType: 'Ancillary',
      hostingType: 'On Premise',
      locationType: 'On-Prem Data Center',
      cmCurrentStatus: 'Live',
      cmDisposition: 'Retire',
      sixRClassification: 'Retire',
      timeClassification: 'Eliminate',
      cmWave: 'Not Scheduled',
      cmEffort: 'Small: 1-2 months',
      drTier: 'Tier 4',
      phi: false,
      rto: '> 96 Hours',
      rpo: '> 24 Hours',
      targetDrStrategy: 'Backup & Restore',
      lifecycleState: 'Phase Out',
    },
  },
  {
    id: 'fst-shared-service',
    builtin: true,
    name: 'Infrastructure / shared service',
    description:
      'Interface engine, imaging archive, print, file services, authentication. Nothing else in its wave can cut over until this does, so it is planned first and moved first.',
    questionnaires: ['qst-tech', 'qst-integration', 'qst-cutover'],
    defaults: {
      applicationType: 'Infrastructure',
      hostingType: 'On Premise',
      locationType: 'On-Prem Data Center',
      targetHosting: 'AWS',
      cmCurrentStatus: 'Live',
      cmDisposition: 'Rehost',
      sixRClassification: 'Rehost',
      timeClassification: 'Invest',
      cmWave: 'Wave 1',
      cmEffort: 'Large: 5+ months',
      drTier: 'Tier 1',
      latencySensitive: true,
      hardcodedIps: true,
      rto: '< 8 Hours',
      rpo: '< 4 hours',
      targetDrStrategy: 'Warm Standby',
      lifecycleState: 'Active',
    },
  },
];

// ---------------------------------------------------------------------------
// STARTER RUNBOOK — the per-application migration checklist, in the order it is
// actually worked. Applied to an application it becomes a live, tickable copy.
// ---------------------------------------------------------------------------
const rbTask = (id, phase, role, task) => ({ id, phase, role, task, done: false });

const STARTER_RUNBOOKS = [
  {
    id: 'rb-standard',
    builtin: true,
    name: 'Standard application migration runbook',
    description:
      'The default per-application checklist. Every line has an owning role, because "the team will handle it" is how a backup goes unverified until the night it is needed.',
    tasks: [
      rbTask('rb-1', 'Pre-migration', 'Application Analyst', 'Confirm vendor sign-off for the target platform and version'),
      rbTask('rb-2', 'Pre-migration', 'Migration Lead', 'Confirm the downtime window with the department and publish it'),
      rbTask('rb-3', 'Pre-migration', 'Change Manager', 'Raise the change record and take it through CAB approval'),
      rbTask('rb-4', 'Pre-migration', 'Backup Engineer', 'Verify a successful full backup AND a test restore of it'),
      rbTask('rb-5', 'Pre-migration', 'Cloud Engineer', 'Build the target environment: servers, storage, licences, agents'),
      rbTask('rb-6', 'Pre-migration', 'Network Engineer', 'Open firewall rules and routes to the target; document every rule'),
      rbTask('rb-7', 'Pre-migration', 'Network Engineer', 'Lower DNS TTL on the application records at least 48 hours ahead'),
      rbTask('rb-8', 'Pre-migration', 'DBA', 'Pre-seed data to the target and validate row counts and checksums'),
      rbTask('rb-9', 'Pre-migration', 'Integration Engineer', 'Stage the interface-engine configuration for the new endpoint'),
      rbTask('rb-10', 'Pre-migration', 'Application Analyst', 'Freeze application and configuration changes'),
      rbTask('rb-11', 'Pre-migration', 'Clinical Owner', 'Confirm downtime procedures are printed and staff are briefed'),
      rbTask('rb-12', 'Pre-migration', 'Migration Lead', 'Publish the runbook, the on-call roster and the bridge details'),

      rbTask('rb-13', 'Cutover', 'Application Analyst', 'Notify users, stop services and take the application offline'),
      rbTask('rb-14', 'Cutover', 'Backup Engineer', 'Take the final backup / snapshot immediately before any change'),
      rbTask('rb-15', 'Cutover', 'DBA', 'Run the final delta data sync and verify counts against source'),
      rbTask('rb-16', 'Cutover', 'Network Engineer', 'Apply DNS / CNAME changes to point at the target'),
      rbTask('rb-17', 'Cutover', 'Network Engineer', 'Apply firewall and NAT changes; remove temporary migration rules'),
      rbTask('rb-18', 'Cutover', 'Integration Engineer', 'Re-point the interface engine to the new host and restart channels'),
      rbTask('rb-19', 'Cutover', 'Application Analyst', 'Start services on the target and confirm licence check-in'),
      rbTask('rb-20', 'Cutover', 'Integration Engineer', 'Verify interfaces flow in both directions with a test message'),
      rbTask('rb-21', 'Cutover', 'Clinical Owner', 'Run the agreed smoke-test script with a named clinical tester'),
      rbTask('rb-22', 'Cutover', 'Migration Lead', 'Go / no-go decision against the rollback trigger and the clock'),

      rbTask('rb-23', 'Post-migration', 'Cloud Engineer', 'Confirm monitoring and alerting are reporting healthy'),
      rbTask('rb-24', 'Post-migration', 'Backup Engineer', 'Confirm the first successful backup on the target'),
      rbTask('rb-25', 'Post-migration', 'Migration Lead', 'Run hypercare for the agreed period and log every issue'),
      rbTask('rb-26', 'Post-migration', 'Application Analyst', 'Update the fact sheet: hosting, disposition, lessons learned'),
      rbTask('rb-27', 'Post-migration', 'Cloud Engineer', 'Power off source servers, hold, then decommission'),
      rbTask('rb-28', 'Post-migration', 'Application Owner', 'Obtain business and technical sign-off'),
      rbTask('rb-29', 'Post-migration', 'Change Manager', 'Close the change record with the actual outcome'),
    ],
  },
];

// ---- Schema-derived helpers ------------------------------------------------
const APP_FIELDS = allFieldsFor('Application');
const APP_FIELD_MAP = APP_FIELDS.reduce((acc, f) => {
  if (!acc[f.key]) acc[f.key] = f;
  return acc;
}, {});
const APP_FIELD_KEYS = Object.keys(APP_FIELD_MAP);
const fieldLabel = (key) => (APP_FIELD_MAP[key] ? APP_FIELD_MAP[key].label : key);

// ---- Small utilities -------------------------------------------------------
const uid = (p) => `${p}-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e4).toString(36)}`;
const readLocal = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (e) { return fallback; }
};
const writeLocal = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* quota */ }
};

const EMPTY_DOC = { questionnaires: [], factSheetTemplates: [], runbooks: [], runbookInstances: [] };
const normaliseDoc = (d) => ({
  questionnaires: Array.isArray(d && d.questionnaires) ? d.questionnaires : [],
  factSheetTemplates: Array.isArray(d && d.factSheetTemplates) ? d.factSheetTemplates : [],
  runbooks: Array.isArray(d && d.runbooks) ? d.runbooks : [],
  runbookInstances: Array.isArray(d && d.runbookInstances) ? d.runbookInstances : [],
});

const upsert = (list, item) => {
  const i = list.findIndex((x) => x.id === item.id);
  if (i < 0) return list.concat(item);
  const next = list.slice();
  next[i] = item;
  return next;
};

// ---- Presentational bits ---------------------------------------------------
function FieldChips({ keys = [] }) {
  if (!keys.length) {
    return <Typography variant="caption" sx={{ color: GREY }}>context only — no field</Typography>;
  }
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {keys.map((k) => (
        <Tooltip key={k} title={APP_FIELD_MAP[k] ? APP_FIELD_MAP[k].section : 'not in the current schema'}>
          <Chip
            size="small"
            label={fieldLabel(k)}
            variant={APP_FIELD_MAP[k] ? 'filled' : 'outlined'}
            sx={{ height: 20, bgcolor: APP_FIELD_MAP[k] ? '#E6ECF3' : 'transparent' }}
          />
        </Tooltip>
      ))}
    </Stack>
  );
}

function Progress({ value, width = 160 }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <LinearProgress
        variant="determinate"
        value={v}
        sx={{
          width, height: 8, borderRadius: 4, bgcolor: '#E6ECF3',
          '& .MuiLinearProgress-bar': { bgcolor: v >= 100 ? '#2E7D32' : BLUE, borderRadius: 4 },
        }}
      />
      <Typography variant="caption" sx={{ color: GREY, minWidth: 34 }}>{v}%</Typography>
    </Stack>
  );
}

function SectionTitle({ icon, children, sub }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        {icon}
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: HEAD }}>{children}</Typography>
      </Stack>
      {sub && <Typography variant="body2" sx={{ color: GREY, mt: 0.5 }}>{sub}</Typography>}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function TemplateLibrary({ tenantId }) {
  const [tab, setTab] = useState(0);
  const [doc, setDoc] = useState(EMPTY_DOC);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [localMode, setLocalMode] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  // tab 1 state
  const [selectedQstId, setSelectedQstId] = useState(STARTER_QUESTIONNAIRES[0].id);
  const [tplDialog, setTplDialog] = useState(null);   // questionnaire meta editor
  const [qDialog, setQDialog] = useState(null);       // single-question editor
  const fileRef = useRef(null);

  // tab 2 state
  const [applyDialog, setApplyDialog] = useState(null); // { template, name }
  const [fstDialog, setFstDialog] = useState(null);     // fact-sheet template editor

  // tab 3 state
  const [runbookId, setRunbookId] = useState(STARTER_RUNBOOKS[0].id);
  const [runbookApp, setRunbookApp] = useState(null);

  const canEdit = isMaster() || tenantId === 'optimum';
  const tenantKey = tenantId || 'default';

  const api = useCallback(async (path, options = {}) => {
    const res = await fetch(`${getConfig().apiUrl}${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getIdToken()}`, ...(options.headers || {}) },
    });
    const text = await res.text();
    // An error envelope is often an HTML page, not JSON. Parsing it bare threw
    // SyntaxError here, before the !res.ok check below could report the real
    // status, so a 502 surfaced to the user as "Unexpected token <".
    let data = {};
    let parseFailed = false;
    try { data = text ? JSON.parse(text) : {}; } catch (e) { parseFailed = true; }
    if (!res.ok) throw new Error(data.error || data.message || `request failed (${res.status})`);
    if (parseFailed) throw new Error(`The API returned ${res.status} with a body that is not JSON.`);
    return data;
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      setApps(await store.listFactSheets(tenantId, { type: 'Application' }));
    } catch (e) {
      setApps([]);
    }
    try {
      const data = await api(`/templates?tenantId=${encodeURIComponent(tenantId || '')}`);
      setDoc(normaliseDoc(Array.isArray(data) ? { questionnaires: data } : data));
      setLocalMode(false);
    } catch (e) {
      setLocalMode(true);
      setDoc(normaliseDoc((readLocal(LS_TEMPLATES, {}) || {})[tenantKey]));
    }
    setLoading(false);
  }, [api, tenantId, tenantKey]);

  useEffect(() => { load(); }, [load]);

  // Write locally first (instant, always works), then write through to the API.
  const persist = useCallback(async (next) => {
    setDoc(next);
    const all = readLocal(LS_TEMPLATES, {}) || {};
    writeLocal(LS_TEMPLATES, { ...all, [tenantKey]: next });
    if (localMode) return;
    setBusy(true);
    try {
      await api('/templates', { method: 'POST', body: JSON.stringify({ tenantId: tenantId || '', ...next }) });
    } catch (e) {
      setLocalMode(true);
    } finally {
      setBusy(false);
    }
  }, [api, localMode, tenantId, tenantKey]);

  // ---- Merged views: compiled-in starters + this tenant's own -------------
  const questionnaires = useMemo(
    () => STARTER_QUESTIONNAIRES.concat(doc.questionnaires),
    [doc.questionnaires]
  );
  const factTemplates = useMemo(
    () => STARTER_FACT_TEMPLATES.concat(doc.factSheetTemplates),
    [doc.factSheetTemplates]
  );
  const runbooks = useMemo(
    () => STARTER_RUNBOOKS.concat(doc.runbooks),
    [doc.runbooks]
  );

  const selectedQst = useMemo(
    () => questionnaires.find((t) => t.id === selectedQstId) || questionnaires[0],
    [questionnaires, selectedQstId]
  );
  const selectedRunbook = useMemo(
    () => runbooks.find((r) => r.id === runbookId) || runbooks[0],
    [runbooks, runbookId]
  );

  const qstName = useCallback(
    (id) => (questionnaires.find((t) => t.id === id) || {}).name || id,
    [questionnaires]
  );

  // ---- Tab 1 actions -------------------------------------------------------
  const duplicateQuestionnaire = (t) => {
    const copy = {
      ...t,
      id: uid('qst'),
      builtin: false,
      basedOn: t.id,
      version: 1,
      name: `${t.name} (copy)`,
      questions: (t.questions || []).map((q) => ({ ...q, id: uid('q') })),
    };
    persist({ ...doc, questionnaires: doc.questionnaires.concat(copy) });
    setSelectedQstId(copy.id);
    setMsg(`Duplicated "${t.name}". The copy is yours to customise.`);
  };

  const saveQuestionnaire = (t) => {
    persist({ ...doc, questionnaires: upsert(doc.questionnaires, { ...t, version: (t.version || 1) + 1 }) });
    setSelectedQstId(t.id);
  };

  const deleteQuestionnaire = (t) => {
    if (!window.confirm(`Delete the "${t.name}" questionnaire from this library?`)) return;
    persist({ ...doc, questionnaires: doc.questionnaires.filter((x) => x.id !== t.id) });
    setSelectedQstId(STARTER_QUESTIONNAIRES[0].id);
  };

  const exportQuestionnaire = (t) => {
    const payload = {
      kind: 'primordia.questionnaire',
      exportedAt: new Date().toISOString(),
      template: { ...t, builtin: false },
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    triggerDownload(blob, `${t.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.json`);
  };

  const importQuestionnaire = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result));
        const t = parsed.template || parsed;
        if (!t || !t.name || !Array.isArray(t.questions)) {
          throw new Error('Not a Primordia questionnaire — expected a name and a questions array.');
        }
        const imported = {
          id: uid('qst'),
          builtin: false,
          version: 1,
          name: t.name,
          category: TEMPLATE_CATEGORIES.includes(t.category) ? t.category : TEMPLATE_CATEGORIES[0],
          description: t.description || '',
          questions: t.questions.map((q) => ({
            id: uid('q'),
            text: String(q.text || ''),
            helper: String(q.helper || ''),
            fields: Array.isArray(q.fields) ? q.fields : [],
          })),
        };
        persist({ ...doc, questionnaires: doc.questionnaires.concat(imported) });
        setSelectedQstId(imported.id);
        setMsg(`Imported "${imported.name}" — ${imported.questions.length} questions.`);
      } catch (e) {
        setErr(`Import failed: ${e.message}`);
      }
    };
    reader.readAsText(file);
  };

  const saveQuestion = () => {
    const t = questionnaires.find((x) => x.id === qDialog.templateId);
    if (!t) return;
    const q = {
      id: qDialog.id || uid('q'),
      text: qDialog.text,
      helper: qDialog.helper,
      fields: qDialog.fields,
    };
    const questions = qDialog.id
      ? (t.questions || []).map((x) => (x.id === q.id ? q : x))
      : (t.questions || []).concat(q);
    saveQuestionnaire({ ...t, questions });
    setQDialog(null);
  };

  const deleteQuestion = (t, q) => {
    saveQuestionnaire({ ...t, questions: (t.questions || []).filter((x) => x.id !== q.id) });
  };

  // ---- Tab 2 actions -------------------------------------------------------
  const duplicateFactTemplate = (t) => {
    const copy = { ...t, id: uid('fst'), builtin: false, basedOn: t.id, name: `${t.name} (copy)`, defaults: { ...t.defaults } };
    persist({ ...doc, factSheetTemplates: doc.factSheetTemplates.concat(copy) });
    setMsg(`Duplicated "${t.name}".`);
  };

  const deleteFactTemplate = (t) => {
    if (!window.confirm(`Delete the "${t.name}" fact sheet template?`)) return;
    persist({ ...doc, factSheetTemplates: doc.factSheetTemplates.filter((x) => x.id !== t.id) });
  };

  const applyFactTemplate = async () => {
    const { template, name } = applyDialog;
    setBusy(true); setErr(''); setMsg('');
    try {
      const created = await store.saveFactSheet({
        type: 'Application',
        tenantId: tenantId || 'optimum',
        name,
        fields: { ...template.defaults, name },
        tags: [],
        relations: {},
        quality: 'Draft',
      });
      setApps(await store.listFactSheets(tenantId, { type: 'Application' }));
      setApplyDialog(null);
      const qs = (template.questionnaires || []).map(qstName).join(', ');
      setMsg(
        `Created "${created ? created.name : name}" from "${template.name}" with ` +
        `${Object.keys(template.defaults || {}).length} fields pre-filled` +
        (qs ? `. Suggested questionnaires: ${qs}.` : '.')
      );
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  // ---- Tab 3 actions -------------------------------------------------------
  const applyRunbook = () => {
    if (!selectedRunbook || !runbookApp) return;
    const instance = {
      id: uid('rbi'),
      templateId: selectedRunbook.id,
      templateName: selectedRunbook.name,
      appId: runbookApp.id,
      appName: runbookApp.name,
      createdAt: new Date().toISOString(),
      tasks: (selectedRunbook.tasks || []).map((t) => ({ ...t, done: false })),
    };
    persist({ ...doc, runbookInstances: doc.runbookInstances.concat(instance) });
    setMsg(`Runbook applied to ${runbookApp.name} — ${instance.tasks.length} tasks.`);
  };

  const toggleTask = (instance, taskId) => {
    const next = {
      ...instance,
      tasks: instance.tasks.map((t) => (t.id === taskId ? { ...t, done: !t.done } : t)),
    };
    persist({ ...doc, runbookInstances: upsert(doc.runbookInstances, next) });
  };

  const deleteInstance = (instance) => {
    if (!window.confirm(`Remove the runbook for ${instance.appName}?`)) return;
    persist({ ...doc, runbookInstances: doc.runbookInstances.filter((x) => x.id !== instance.id) });
  };

  const exportRunbookCsv = (tasks, filename) => {
    const rows = tasks.map((t, i) => ({ ...t, order: i + 1 }));
    exportCsv(filename, rows, [
      { label: '#', value: 'order' },
      { label: 'Phase', value: 'phase' },
      { label: 'Task', value: 'task' },
      { label: 'Owner role', value: 'role' },
      { label: 'Done', value: (r) => (r.done ? 'Yes' : 'No') },
    ]);
  };

  // ---- Render --------------------------------------------------------------
  if (loading) {
    return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box>;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <LibraryBooksIcon sx={{ color: HEAD }} />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Template Library</Typography>
        <HelpBubble topic="templateLibrary" />
        <Chip
          size="small"
          icon={canEdit ? undefined : <LockIcon sx={{ fontSize: 14 }} />}
          label={canEdit ? 'Optimum-owned · editable' : 'Read-only · view and apply'}
          sx={{ bgcolor: '#eef3ff', color: '#0B5FFF' }}
        />
        {busy && <CircularProgress size={16} />}
      </Stack>
      <Typography variant="body2" sx={{ color: GREY, mb: 2 }}>
        Optimum's discovery methodology, written down once and reused on every engagement. Questions map to
        fact-sheet fields, so an answer lands in the CMDB instead of in a document. Clients can read and apply
        the library; only Optimum edits it.
      </Typography>

      {localMode && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Your edits are saved in this browser only — the <code>/templates</code> API is not deployed. That
          covers new and edited question sets, fact-sheet templates, applied runbooks and their tick state:
          a colleague signing in sees the built-in library, not your copy. Export a template as JSON, or a
          runbook as CSV, to hand your work to someone else. Applying a fact-sheet template is the exception:
          it creates a real fact sheet, and that is stored on the server as usual.
        </Alert>
      )}
      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}
      {msg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg('')}>{msg}</Alert>}

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2, borderBottom: '1px solid #E0E6ED' }}>
        <Tab label={<TabLabel text="Discovery questionnaires" topic="tabDiscoveryQuestionnaires" />} />
        <Tab label={<TabLabel text="Fact sheet templates" topic="tabFactSheetTemplates" />} />
        <Tab label={<TabLabel text="Runbook checklist" topic="tabRunbookChecklist" />} />
      </Tabs>

      {/* ------------------------------------------------------------------ */}
      {/* TAB 1 — Discovery questionnaires                                    */}
      {/* ------------------------------------------------------------------ */}
      {tab === 0 && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
          <Paper sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0 }}>
            <Box sx={{ p: 1.5 }}>
              <SectionTitle>Question sets</SectionTitle>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  disabled={!canEdit}
                  onClick={() => setTplDialog({
                    id: uid('qst'), name: '', description: '', category: TEMPLATE_CATEGORIES[0],
                    questions: [], builtin: false, version: 0, isNew: true,
                  })}
                >
                  New
                </Button>
                <Button size="small" startIcon={<FileUploadIcon />} disabled={!canEdit}
                  onClick={() => fileRef.current && fileRef.current.click()}>
                  Import
                </Button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="application/json,.json"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files && e.target.files[0];
                    if (f) importQuestionnaire(f);
                    e.target.value = '';
                  }}
                />
              </Stack>
            </Box>
            <Divider />
            <List dense disablePadding>
              {TEMPLATE_CATEGORIES.concat(['Other']).map((cat) => {
                const inCat = questionnaires.filter(
                  (t) => (TEMPLATE_CATEGORIES.includes(t.category) ? t.category : 'Other') === cat
                );
                if (!inCat.length) return null;
                return (
                  <Box key={cat}>
                    <Typography variant="caption" sx={{ px: 1.5, pt: 1, display: 'block', fontWeight: 700, color: HEAD }}>
                      {cat}
                    </Typography>
                    {inCat.map((t) => (
                      <ListItemButton
                        key={t.id}
                        selected={selectedQst && t.id === selectedQst.id}
                        onClick={() => setSelectedQstId(t.id)}
                      >
                        <ListItemText
                          primary={t.name}
                          secondary={`${(t.questions || []).length} questions${t.builtin ? ' · Optimum standard' : ' · custom'}`}
                          primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
                          secondaryTypographyProps={{ fontSize: 12 }}
                        />
                      </ListItemButton>
                    ))}
                  </Box>
                );
              })}
            </List>
          </Paper>

          <Paper sx={{ flex: 1, p: 2, width: '100%' }}>
            {selectedQst && (
              <>
                <Stack direction="row" alignItems="flex-start" spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: HEAD }}>{selectedQst.name}</Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5, mb: 1 }} flexWrap="wrap" useFlexGap>
                      <Chip size="small" label={selectedQst.category || 'Other'} sx={{ height: 20 }} />
                      <Chip size="small" variant="outlined" sx={{ height: 20 }}
                        label={selectedQst.builtin ? 'Optimum standard' : `custom · v${selectedQst.version || 1}`} />
                      <Chip size="small" variant="outlined" sx={{ height: 20 }}
                        label={`${(selectedQst.questions || []).length} questions`} />
                    </Stack>
                    <Typography variant="body2" sx={{ color: GREY }}>{selectedQst.description}</Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Tooltip title="Duplicate to customise">
                      <span>
                        <Button size="small" startIcon={<ContentCopyIcon />} disabled={!canEdit}
                          onClick={() => duplicateQuestionnaire(selectedQst)}>
                          Duplicate
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip title="Export as JSON to share">
                      <Button size="small" startIcon={<FileDownloadIcon />} onClick={() => exportQuestionnaire(selectedQst)}>
                        Export
                      </Button>
                    </Tooltip>
                    {!selectedQst.builtin && (
                      <>
                        <Button size="small" startIcon={<EditIcon />} disabled={!canEdit}
                          onClick={() => setTplDialog({ ...selectedQst, isNew: false })}>
                          Edit
                        </Button>
                        <Button size="small" color="error" startIcon={<DeleteIcon />} disabled={!canEdit}
                          onClick={() => deleteQuestionnaire(selectedQst)}>
                          Delete
                        </Button>
                      </>
                    )}
                  </Stack>
                </Stack>

                {selectedQst.builtin && canEdit && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Optimum standard sets are read-only so every engagement starts from the same baseline.
                    Duplicate it to tailor the wording for this client.
                  </Alert>
                )}

                <Divider sx={{ my: 2 }} />

                <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: HEAD, flex: 1 }}>
                    Questions and the fields they fill in
                  </Typography>
                  {!selectedQst.builtin && (
                    <Button size="small" startIcon={<AddIcon />} disabled={!canEdit}
                      onClick={() => setQDialog({ templateId: selectedQst.id, id: null, text: '', helper: '', fields: [] })}>
                      Add question
                    </Button>
                  )}
                </Stack>

                <Table size="small">
                  <TableHead>
                    <TableRow sx={headSx}>
                      <TableCell sx={{ width: 40 }}>#</TableCell>
                      <TableCell>Question</TableCell>
                      <TableCell sx={{ width: '32%' }}>Maps to fact-sheet fields</TableCell>
                      {!selectedQst.builtin && <TableCell align="right" sx={{ width: 90 }}>Edit</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(selectedQst.questions || []).map((q, i) => (
                      <TableRow key={q.id} hover>
                        <TableCell sx={{ color: GREY }}>{i + 1}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{q.text}</Typography>
                          {q.helper && (
                            <Typography variant="caption" sx={{ color: GREY }}>{q.helper}</Typography>
                          )}
                        </TableCell>
                        <TableCell><FieldChips keys={q.fields || []} /></TableCell>
                        {!selectedQst.builtin && (
                          <TableCell align="right">
                            <IconButton size="small" disabled={!canEdit}
                              onClick={() => setQDialog({ templateId: selectedQst.id, ...q })}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" disabled={!canEdit} onClick={() => deleteQuestion(selectedQst, q)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                    {!(selectedQst.questions || []).length && (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ color: GREY, textAlign: 'center', py: 3 }}>
                          No questions yet.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </>
            )}
          </Paper>
        </Stack>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* TAB 2 — Fact sheet templates                                        */}
      {/* ------------------------------------------------------------------ */}
      {tab === 1 && (
        <Box>
          <Stack direction="row" alignItems="flex-start" sx={{ mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <SectionTitle
                icon={<DescriptionIcon sx={{ color: HEAD }} />}
                sub="A new application should not start as an empty form. Pick the shape it is, and hosting, likely disposition, DR tier and the questions to ask are already there — the analyst corrects what is wrong instead of typing what is obvious."
              >
                Starting points for a new application
              </SectionTitle>
            </Box>
            <Button
              size="small"
              startIcon={<AddIcon />}
              disabled={!canEdit}
              onClick={() => setFstDialog({
                id: uid('fst'), name: '', description: '', questionnaires: [], defaults: {}, builtin: false, isNew: true,
              })}
            >
              New template
            </Button>
          </Stack>

          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="stretch">
            {factTemplates.map((t) => (
              <Paper key={t.id} sx={{ p: 2, width: { xs: '100%', md: 420 }, display: 'flex', flexDirection: 'column' }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: HEAD, flex: 1 }}>{t.name}</Typography>
                  <Chip size="small" variant="outlined" sx={{ height: 20 }} label={t.builtin ? 'standard' : 'custom'} />
                </Stack>
                <Typography variant="body2" sx={{ color: GREY, my: 1, flex: 1 }}>{t.description}</Typography>

                <Typography variant="caption" sx={{ fontWeight: 700, color: HEAD }}>Pre-filled defaults</Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5, mb: 1 }}>
                  {Object.entries(t.defaults || {}).map(([k, v]) => (
                    <Tooltip key={k} title={fieldLabel(k)}>
                      <Chip
                        size="small"
                        label={`${fieldLabel(k)}: ${typeof v === 'boolean' ? (v ? 'Yes' : 'No') : v}`}
                        sx={{ height: 20, bgcolor: '#E6ECF3' }}
                      />
                    </Tooltip>
                  ))}
                </Stack>

                <Typography variant="caption" sx={{ fontWeight: 700, color: HEAD }}>Discovery questions to run</Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5, mb: 1.5 }}>
                  {(t.questionnaires || []).map((id) => (
                    <Chip key={id} size="small" variant="outlined" label={qstName(id)} sx={{ height: 20 }} />
                  ))}
                  {!(t.questionnaires || []).length && (
                    <Typography variant="caption" sx={{ color: GREY }}>none linked</Typography>
                  )}
                </Stack>

                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="contained" sx={btnSx} startIcon={<PlayArrowIcon />}
                    onClick={() => setApplyDialog({ template: t, name: '' })}>
                    Apply
                  </Button>
                  <Button size="small" startIcon={<ContentCopyIcon />} disabled={!canEdit}
                    onClick={() => duplicateFactTemplate(t)}>
                    Duplicate
                  </Button>
                  {!t.builtin && (
                    <>
                      <Button size="small" startIcon={<EditIcon />} disabled={!canEdit}
                        onClick={() => setFstDialog({ ...t, isNew: false })}>
                        Edit
                      </Button>
                      <Button size="small" color="error" startIcon={<DeleteIcon />} disabled={!canEdit}
                        onClick={() => deleteFactTemplate(t)}>
                        Delete
                      </Button>
                    </>
                  )}
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* TAB 3 — Runbook checklist                                           */}
      {/* ------------------------------------------------------------------ */}
      {tab === 2 && (
        <Box>
          <SectionTitle
            icon={<PlaylistAddCheckIcon sx={{ color: HEAD }} />}
            sub="One checklist per application, in the order it is actually worked, with an owning role on every line. Apply it to an application, tick it off on the night, export it to CSV for the cutover call."
          >
            Migration runbook
          </SectionTitle>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
              <TextField
                select size="small" label="Runbook" sx={{ minWidth: 320 }}
                value={selectedRunbook ? selectedRunbook.id : ''}
                onChange={(e) => setRunbookId(e.target.value)}
              >
                {runbooks.map((r) => (
                  <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                ))}
              </TextField>
              <Autocomplete
                size="small"
                sx={{ minWidth: 320 }}
                options={apps}
                getOptionLabel={(a) => a.name || ''}
                value={runbookApp}
                onChange={(e, v) => setRunbookApp(v)}
                renderInput={(params) => <TextField {...params} label="Apply to application" />}
              />
              <Button variant="contained" sx={btnSx} startIcon={<PlayArrowIcon />}
                disabled={!runbookApp} onClick={applyRunbook}>
                Apply runbook
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button size="small" startIcon={<FileDownloadIcon />}
                onClick={() => exportRunbookCsv(
                  (selectedRunbook && selectedRunbook.tasks) || [],
                  'migration-runbook-template.csv'
                )}>
                Export blank CSV
              </Button>
            </Stack>
            {selectedRunbook && (
              <Typography variant="body2" sx={{ color: GREY, mt: 1.5 }}>{selectedRunbook.description}</Typography>
            )}
          </Paper>

          {/* The master checklist */}
          <Paper sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={headSx}>
                  <TableCell sx={{ width: 40 }}>#</TableCell>
                  <TableCell sx={{ width: 150 }}>Phase</TableCell>
                  <TableCell>Task</TableCell>
                  <TableCell sx={{ width: 200 }}>Owner role</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {((selectedRunbook && selectedRunbook.tasks) || []).map((t, i) => (
                  <TableRow key={t.id} hover>
                    <TableCell sx={{ color: GREY }}>{i + 1}</TableCell>
                    <TableCell>
                      <Chip size="small" label={t.phase}
                        sx={{ height: 20, bgcolor: 'transparent', border: `1px solid ${PHASE_COLOR[t.phase] || GREY}`, color: PHASE_COLOR[t.phase] || GREY, fontWeight: 600 }} />
                    </TableCell>
                    <TableCell>{t.task}</TableCell>
                    <TableCell sx={{ color: GREY }}>{t.role}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          {/* Live, applied runbooks */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: HEAD, mb: 1 }}>
            Applied runbooks ({doc.runbookInstances.length})
          </Typography>
          {!doc.runbookInstances.length && (
            <Typography variant="body2" sx={{ color: GREY, mb: 2 }}>
              None yet. Pick an application above and apply the runbook to get a tickable copy.
            </Typography>
          )}
          {doc.runbookInstances.map((inst) => {
            const done = inst.tasks.filter((t) => t.done).length;
            const pct = inst.tasks.length ? Math.round((done / inst.tasks.length) * 100) : 0;
            return (
              <Accordion key={inst.id} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%', pr: 2 }}>
                    <Typography sx={{ fontWeight: 700, color: HEAD, flex: 1 }}>{inst.appName}</Typography>
                    <Typography variant="caption" sx={{ color: GREY }}>{done} / {inst.tasks.length} done</Typography>
                    <Progress value={pct} />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                    <Button size="small" startIcon={<FileDownloadIcon />}
                      onClick={() => exportRunbookCsv(inst.tasks, `runbook-${inst.appName.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.csv`)}>
                      Export CSV
                    </Button>
                    <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => deleteInstance(inst)}>
                      Remove
                    </Button>
                  </Stack>
                  {PHASES.map((phase) => {
                    const tasks = inst.tasks.filter((t) => t.phase === phase);
                    if (!tasks.length) return null;
                    return (
                      <Box key={phase} sx={{ mb: 1.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: PHASE_COLOR[phase] || HEAD }}>
                          {phase}
                        </Typography>
                        <Table size="small">
                          <TableBody>
                            {tasks.map((t) => (
                              <TableRow key={t.id} hover>
                                <TableCell sx={{ width: 48 }}>
                                  <Checkbox size="small" checked={!!t.done} onChange={() => toggleTask(inst, t.id)} />
                                </TableCell>
                                <TableCell sx={{ textDecoration: t.done ? 'line-through' : 'none', color: t.done ? GREY : 'inherit' }}>
                                  {t.task}
                                </TableCell>
                                <TableCell sx={{ width: 200, color: GREY }}>{t.role}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}

      {/* ---- Questionnaire meta dialog ---------------------------------- */}
      <Dialog open={!!tplDialog} onClose={() => setTplDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700, color: HEAD }}>
          {tplDialog && tplDialog.isNew ? 'New questionnaire' : 'Edit questionnaire'}
        </DialogTitle>
        {tplDialog && (
          <DialogContent dividers>
            <TextField fullWidth size="small" label="Name" sx={{ mb: 2 }} value={tplDialog.name}
              onChange={(e) => setTplDialog({ ...tplDialog, name: e.target.value })} />
            <TextField select fullWidth size="small" label="Category" sx={{ mb: 2 }} value={tplDialog.category}
              onChange={(e) => setTplDialog({ ...tplDialog, category: e.target.value })}>
              {TEMPLATE_CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <TextField fullWidth size="small" multiline minRows={3} label="Description"
              helperText="Why this set exists and who answers it."
              value={tplDialog.description}
              onChange={(e) => setTplDialog({ ...tplDialog, description: e.target.value })} />
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setTplDialog(null)}>Cancel</Button>
          <Button variant="contained" sx={btnSx} disabled={!tplDialog || !tplDialog.name}
            onClick={() => {
              const { isNew, ...t } = tplDialog;
              saveQuestionnaire(t);
              setTplDialog(null);
            }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Question dialog -------------------------------------------- */}
      <Dialog open={!!qDialog} onClose={() => setQDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700, color: HEAD }}>
          {qDialog && qDialog.id ? 'Edit question' : 'Add question'}
        </DialogTitle>
        {qDialog && (
          <DialogContent dividers>
            <TextField fullWidth size="small" multiline minRows={2} label="Question" sx={{ mb: 2 }}
              value={qDialog.text} onChange={(e) => setQDialog({ ...qDialog, text: e.target.value })} />
            <TextField fullWidth size="small" label="Helper text" sx={{ mb: 2 }}
              helperText="Plain English — what a good answer looks like."
              value={qDialog.helper} onChange={(e) => setQDialog({ ...qDialog, helper: e.target.value })} />
            <Autocomplete
              multiple size="small" options={APP_FIELD_KEYS}
              getOptionLabel={(k) => `${fieldLabel(k)} (${k})`}
              value={qDialog.fields || []}
              onChange={(e, v) => setQDialog({ ...qDialog, fields: v })}
              renderInput={(params) => (
                <TextField {...params} label="Fact-sheet fields this answer fills in"
                  helperText="Leave empty if the answer is context we keep rather than a field we store." />
              )}
            />
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setQDialog(null)}>Cancel</Button>
          <Button variant="contained" sx={btnSx} disabled={!qDialog || !qDialog.text} onClick={saveQuestion}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* ---- Fact sheet template dialog --------------------------------- */}
      <Dialog open={!!fstDialog} onClose={() => setFstDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700, color: HEAD }}>
          {fstDialog && fstDialog.isNew ? 'New fact sheet template' : 'Edit fact sheet template'}
        </DialogTitle>
        {fstDialog && (
          <DialogContent dividers>
            <TextField fullWidth size="small" label="Name" sx={{ mb: 2 }} value={fstDialog.name}
              onChange={(e) => setFstDialog({ ...fstDialog, name: e.target.value })} />
            <TextField fullWidth size="small" multiline minRows={2} label="Description" sx={{ mb: 2 }}
              value={fstDialog.description}
              onChange={(e) => setFstDialog({ ...fstDialog, description: e.target.value })} />
            <Autocomplete
              multiple size="small" sx={{ mb: 2 }}
              options={questionnaires.map((q) => q.id)}
              getOptionLabel={qstName}
              value={fstDialog.questionnaires || []}
              onChange={(e, v) => setFstDialog({ ...fstDialog, questionnaires: v })}
              renderInput={(params) => <TextField {...params} label="Discovery questions to run" />}
            />
            <Typography variant="caption" sx={{ fontWeight: 700, color: HEAD }}>Pre-filled defaults</Typography>
            <Typography variant="caption" sx={{ display: 'block', color: GREY, mb: 1 }}>
              One <code>fieldKey=value</code> per line, e.g. <code>hostingType=SaaS</code>. Booleans accept true / false.
            </Typography>
            <TextField
              fullWidth size="small" multiline minRows={6}
              value={Object.entries(fstDialog.defaults || {}).map(([k, v]) => `${k}=${v}`).join('\n')}
              onChange={(e) => {
                const defaults = {};
                String(e.target.value).split('\n').forEach((line) => {
                  const i = line.indexOf('=');
                  if (i <= 0) return;
                  const k = line.slice(0, i).trim();
                  const raw = line.slice(i + 1).trim();
                  if (!k) return;
                  defaults[k] = raw === 'true' ? true : raw === 'false' ? false : raw;
                });
                setFstDialog({ ...fstDialog, defaults });
              }}
            />
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setFstDialog(null)}>Cancel</Button>
          <Button variant="contained" sx={btnSx} disabled={!fstDialog || !fstDialog.name}
            onClick={() => {
              const { isNew, ...t } = fstDialog;
              persist({ ...doc, factSheetTemplates: upsert(doc.factSheetTemplates, { ...t, builtin: false }) });
              setFstDialog(null);
            }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Apply fact sheet template dialog ---------------------------- */}
      <Dialog open={!!applyDialog} onClose={() => !busy && setApplyDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700, color: HEAD }}>
          {applyDialog ? `Create an application from "${applyDialog.template.name}"` : ''}
        </DialogTitle>
        {applyDialog && (
          <DialogContent dividers>
            <TextField
              fullWidth size="small" autoFocus label="Application name" sx={{ mb: 2 }}
              value={applyDialog.name}
              onChange={(e) => setApplyDialog({ ...applyDialog, name: e.target.value })}
            />
            <Typography variant="caption" sx={{ fontWeight: 700, color: HEAD }}>
              These fields will be populated on the new fact sheet
            </Typography>
            <Table size="small" sx={{ mt: 0.5 }}>
              <TableBody>
                {Object.entries(applyDialog.template.defaults || {}).map(([k, v]) => (
                  <TableRow key={k}>
                    <TableCell sx={{ color: GREY, width: '55%' }}>{fieldLabel(k)}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Alert severity="info" sx={{ mt: 2 }}>
              Defaults are a starting point, not the truth. The analyst confirms or corrects each one during
              discovery — that is faster than typing them from an empty form.
            </Alert>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setApplyDialog(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" sx={btnSx} disabled={busy || !applyDialog || !applyDialog.name}
            onClick={applyFactTemplate}>
            {busy ? 'Creating…' : 'Create fact sheet'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
